import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { createBuilding } from "@/api/buildings/create-building";
import { getCompany } from "@/api/company/get-Company";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogTrigger } from "@radix-ui/react-dialog";
import BuildingTable from "@/components/buildings/buildingTable";
import { queryClient } from "@/lib/reactQuery";
import {
  useSelectedBuildingName,
  useSelectedState,
  useTempSelectedBuildingName,
} from "@/components/buildings/buildingStates";
import { useDialog } from "@/components/utils/isDialogOpen";
import { fetchBuildings } from "@/components/buildings/getBuilding";
import { usePageState, useSetPage } from "@/components/utils/paginationStates";
import { Pagination } from "@/components/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";

const createBuildingSchema = z.object({
  buildingName: z.string({ message: "Enter Building Name" }),
  address: z.string({ message: "Enter Building Address" }),
  letterIdBuilding: z.string({ message: "Enter letter id building" }),
  buildingCity: z.string(),
  buildingState: z.string(),
  buildingZip: z.string(),
});

type CreateBuildingType = z.infer<typeof createBuildingSchema>;

export function RegisterBuilding() {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateBuildingType>({
    resolver: zodResolver(createBuildingSchema),
  });

  const { mutateAsync: CreateBuildingFn } = useMutation({
    mutationKey: ["buildings"],
    mutationFn: createBuilding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buildings"] }); // Refetch the expenses to update the table
    },
  });
  const { setSelectedBuilding } = useSelectedState();
  const { isDialogOpen, setIsDialogOpen } = useDialog();
  const { buildings, currentPage } = fetchBuildings();
  const { setCurrentPage } = useSetPage();
  const { setPageSize } = usePageState();
  const { setSelectedBuildingName, selectedBuildingName } =
    useSelectedBuildingName();
  const { tempSelectedBuildingName, setTempSelectedBuildingName } =
    useTempSelectedBuildingName();

  const totalPages = buildings?.totalPages || 1;
  const totalItems = buildings?.totalBuildings || 0;

  function handleFilterSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSelectedBuildingName(tempSelectedBuildingName); // Set selectedMonth only when 'Apply Filter' is pressed
  }

  async function handleCreateBuilding(data: CreateBuildingType) {
    try {
      const companyRes = await getCompany(); // fetch the entire company object
      const companyId = companyRes.company[0].id;

      const { status } = await CreateBuildingFn({
        buildingName: data.buildingName,
        address: data.address,
        letterIdBuilding: data.letterIdBuilding,
        buildingCity: data.buildingCity,
        buildingState: data.buildingState,
        buildingZip: data.buildingZip,
        companyId: companyId,
      });
      if (status === 201) {
        toast.success("Building created successfully");
        reset();
      }
    } catch (err) {
      console.log(err);
      toast.error("Error creating building");
    }
  }

  const states = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];

  return (
    <div>
      <div className="flex h-14 items-center rounded-b-2xl bg-[#16A34A] bg-opacity-40 px-10">
        <h3 className="p-5 font-Hemmers text-3xl text-black">Customers</h3>
      </div>
      <div className="py-2">
        <div className="flex items-center justify-between p-4">
          {/* Left-aligned New + button with its own form */}
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setSelectedBuilding(null); // Reset selectedExpense when dialog is closed
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => setSelectedBuilding(null)}
                className="gap-x-2 border border-black bg-[#FAFAFA] text-lg text-black hover:bg-gray-200"
              >
                <Plus className="size-4" /> Create New
              </Button>
            </DialogTrigger>
            <DialogContent className="border-transparent bg-inherit">
              <DialogHeader>
                <DialogTitle className="text-black">
                  Register New Customer
                </DialogTitle>
                <DialogDescription>
                  Fill in to register a new customer
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(handleCreateBuilding)}>
                <div className="flex space-x-4">
                  {/* Container for building name input */}
                  <div className="flex w-96 flex-col">
                    <label
                      htmlFor="buildingName"
                      className="block text-sm font-medium text-black"
                    >
                      Customer Name
                    </label>
                    <Input
                      {...register("buildingName", {
                        required: "Building name is required",
                      })}
                      type="text"
                      id="buildingName"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errors.buildingName && (
                      <span className="text-sm text-rose-500">
                        {errors.buildingName.message}
                      </span>
                    )}
                  </div>

                  {/* Container for letter ID input */}
                  <div className="flex w-16 flex-col">
                    <label
                      htmlFor="letterIdBuilding"
                      className="block text-sm font-medium text-black"
                    >
                      Letter Id
                    </label>
                    <Input
                      {...register("letterIdBuilding", {
                        required: "Building letter id is required",
                      })}
                      type="text"
                      id="letterIdBuilding"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errors.letterIdBuilding && (
                      <span className="text-sm text-rose-500">
                        {errors.letterIdBuilding.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Container for address input */}
                <div className="mt-4">
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-black"
                  >
                    Address
                  </label>
                  <Input
                    {...register("address", {
                      required: "Building address is required",
                    })}
                    type="text"
                    id="address"
                    autoComplete="off"
                    className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                  />
                  {errors.address && (
                    <span className="text-sm text-rose-500">
                      {errors.address.message}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex space-x-4">
                  <div>
                    <label
                      htmlFor="state"
                      className="block text-sm font-medium text-black"
                    >
                      State
                    </label>
                    <Controller
                      name="buildingState"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={`mt-1 w-[80px] rounded-md border-zinc-300 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm ${
                              field.value ? "text-zinc-900" : "text-gray-500"
                            }`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="w-[80px]">
                            {states.map((state) => (
                              <SelectItem key={state.value} value={state.label}>
                                {state.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-black"
                    >
                      City
                    </label>
                    <Input
                      type="text"
                      id="buildingCity"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                      {...register("buildingCity")}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="zip"
                      className="block text-sm font-medium text-black"
                    >
                      Zip
                    </label>
                    <Input
                      type="text"
                      id="buildingZip"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                      {...register("buildingZip")}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Button
                    disabled={isSubmitting}
                    className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                    type="submit"
                    onClick={async () => setIsDialogOpen(false)}
                  >
                    Register Customer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Right-aligned Edit and Delete buttons with separate forms */}
          <div className="ml-auto flex items-center gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button className="border border-black bg-[#FAFAFA] text-lg text-black hover:bg-gray-200">
                  Add Filter
                </Button>
              </PopoverTrigger>
              <PopoverContent className="bg-[#FAFAFA] p-4">
                <form onSubmit={handleFilterSubmit}>
                  <div className="mt-4 w-full">
                    <label
                      htmlFor="filterBuilding"
                      className="block text-sm font-medium text-black"
                    >
                      Select Customer
                    </label>
                    <Select
                      onValueChange={(value) =>
                        setTempSelectedBuildingName(value || undefined)
                      }
                      value={tempSelectedBuildingName ?? ""}
                    >
                      <SelectTrigger className="mt-1 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm">
                        <SelectValue placeholder="All Customers" />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings?.buildings.map((buildings) => (
                          <SelectItem
                            key={buildings.buildingName}
                            value={buildings.buildingName}
                          >
                            {buildings.buildingName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-4">
                    <div className="flex gap-x-1">
                      <Button
                        className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                        type="submit"
                      >
                        Apply Filter
                      </Button>
                      {selectedBuildingName && (
                        <Button
                          className="border border-black bg-[#FAFAFA] text-black hover:bg-gray-200"
                          onClick={() => {
                            setTempSelectedBuildingName(undefined);
                            setSelectedBuildingName(undefined);
                          }}
                        >
                          Clear Filter
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      <div className="mx-4 rounded-lg border border-black bg-[#FAFAFA] p-4">
        <BuildingTable></BuildingTable>
        <Pagination
          items={totalItems}
          page={currentPage}
          pages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        ></Pagination>
      </div>
    </div>
  );
}
